<script setup lang="ts">
import { RouterLink } from "vue-router";

defineProps<{
  title: string;
  ready?: boolean;
  mediaType?: "song" | "video";
  linkClass?: string;
  seeAllUrl?: string;
  noControls?: boolean;
  noLinks?: boolean;
  items: ({
    id: string;
    to?: string;
    url?: string;
    dataSongNumber?: string;
    dataSlug?: string;
    imgUrl?: string;
    title?: string;
    subtitle?: string;
    previewAudioUrl?: string;
  } & any)[];
}>();

const emit = defineEmits<{
  (e: "click", ind: number): void;
}>();

function onClick(evt: Event, index: number, url?: string) {
  if (!url) {
    evt.stopImmediatePropagation();
    evt.preventDefault();
  }
  emit("click", index);
  if (!url) {
    return false;
  }
}

const pointerAvailable = !window.matchMedia("(any-hover: none)").matches;

const alwaysShowId = ref("");

onMounted(() => {
  emitter.on("alwaysShowId", (id) => (alwaysShowId.value = id));
});
</script>

<template>
  <section>
    <HomeHeader
      class="ga-section-home-section-header-link"
      :url="seeAllUrl"
    >
      <slot name="title">{{ title }}</slot>
    </HomeHeader>
    <MediaSlider v-if="ready">
      <MediaSliderItem
        v-for="(i, ind) of items"
        :key="i.id"
        class="slider-item ga-home-horizontal-link"
        :data-menu-item="title + ' - ' + i.title"
      >
        <component
          :is="noLinks ? 'div' : i.to ? RouterLink : 'a'"
          :to="i.to"
          :href="i.url || i.to || '#'"
          class="item home-horizontal-scroll-item"
          :class="`${linkClass || ''} item-${mediaType}`"
          :data-songnumber="i.dataSongNumber"
          :data-slug="i.dataSlug"
          :target="i.url ? '_blank' : undefined"
          @click="(evt: Event) => onClick(evt, ind, i.url)"
        >
          <slot
            name="main"
            :data="i"
            :index="ind"
          >
            <div class="artwork">
              <slot
                name="artwork"
                :data="i"
                :index="ind"
              >
                <AlbumArtwork
                  :url="i.imgUrl"
                  :media-type="mediaType"
                  :restrict-width="250"
                />
              </slot>
              <div
                v-if="!noControls && pointerAvailable"
                class="controls"
                :class="{ 'always-show': i.id === alwaysShowId }"
              >
                <div
                  v-if="i.previewAudioUrl"
                  class="control-button"
                >
                  <SongRowPreview
                    :title="i.title"
                    :show-id="i.id"
                    :preview-url="i.previewAudioUrl"
                  />
                </div>
                <div v-else />
                <div class="control-button">
                  <AddToFavorites
                    fit-to-container
                    :show-id="i.id"
                    :song-number="i.dataSongNumber"
                    :title="i.title"
                  />
                </div>
              </div>
            </div>

            <div
              v-if="i.title"
              class="after-artwork"
              :class="{ 'has-preview': i.previewAudioUrl }"
            >
              <div class="labels">
                <div
                  class="title"
                  :title="i.title"
                  :class="{
                    'asian-text': hasAsianText(i.title),
                  }"
                >
                  {{ i.title }}
                </div>
                <div
                  v-if="i.subtitle"
                  class="subtitle"
                  :title="i.subtitle"
                  :class="{
                    'asian-text': hasAsianText(i.subtitle),
                  }"
                >
                  {{ i.subtitle }}
                </div>
              </div>
              <slot
                name="text-end"
                :data="i"
              />
            </div>
          </slot>
        </component>
      </MediaSliderItem>
      <MediaSliderItem
        v-if="seeAllUrl"
        class="slider-item"
      >
        <component
          :is="seeAllUrl.startsWith('http') ? 'a' : 'router-link'"
          :to="(!seeAllUrl.startsWith('http') && seeAllUrl) || undefined"
          :href="seeAllUrl"
          :target="(seeAllUrl.startsWith('http') && '_blank') || undefined"
          class="item"
          :class="`${linkClass || ''} item-${mediaType}`"
        >
          <div class="artwork">
            <div class="see-more-artwork">
              {{ $t("S.LIT_CCLITop100SeeMore") }}
              <span class="sr-only">results from "{{ title }}"</span
              ><FAIcon class="fa-light fa-chevron-right" />
            </div>
          </div>
        </component>
      </MediaSliderItem>
    </MediaSlider>
    <MediaSlider v-else>
      <MediaSliderItem
        v-for="(m, i) of 10"
        :key="i"
        class="not-ready-items"
      >
        <span :class="`item item-${mediaType}`">
          <div class="artwork">
            <div
              class="placeholder"
              :class="`placeholder-${mediaType}`"
            />
          </div>

          <div class="after-artwork">
            <div class="labels">
              <div class="title">
                <span class="placeholder" />
              </div>
              <div class="subtitle">
                <span class="placeholder" />
              </div>
            </div>
          </div>
        </span>
      </MediaSliderItem>
    </MediaSlider>
  </section>
</template>

<style lang="postcss">
.home-horizontal-scroll-item {
  &:hover,
  &:focus-visible {
    .artwork {
      @apply brightness-90;
    }
    .controls {
      @apply opacity-100;
    }
  }
}
</style>
<style scoped lang="postcss">
a.item {
  @apply cursor-pointer transition-all;
  .artwork {
    @apply transition-all;
  }
}
.item {
  --size: 8rem;
  @media screen(xs) {
    --size: 12rem;
  }
  @apply block;
  max-width: var(--size);

  .artwork {
    height: var(--size);
    width: var(--size);
    @apply relative;
  }
}
.item.item-video {
  height: 100%;
  width: 100%;
  max-width: calc(var(--size) * 16 / 9);
  .artwork {
    width: calc(var(--size) * 16 / 9);
    height: var(--size);
  }
}
.item.item-banner {
  height: 100%;
  width: 100%;
  max-width: calc(var(--size) * 18 / 9);
  height: var(--size);
}

.after-artwork {
  @apply mt-1 flex max-w-full items-center justify-between gap-2 leading-4;
}
.labels {
  @apply flex-1;
}
.title {
  @apply line-clamp-1 leading-5;
  &:not(.asian-text) {
    padding-bottom: 0.14em;
  }
}
.subtitle {
  @apply line-clamp-1 text-sm leading-5 text-gray-500;
  &:not(.asian-text) {
    padding-bottom: 0.14em;
  }
}

.artwork .placeholder {
  @apply block w-full rounded;
}
.placeholder-song {
  @apply aspect-square;
}

.placeholder-video {
  @apply aspect-video;
  width: 100%;
  max-width: calc(var(--size) * 16 / 9);
}
.title .placeholder {
  width: 100px;
  height: 1em;
}
.subtitle .placeholder {
  width: 150px;
  height: 1em;
}
.see-more-artwork {
  @apply flex h-full w-full items-center justify-center gap-2 rounded-lg bg-light-700 font-semibold uppercase tracking-wide text-primary transition-all hover:brightness-90;
}

.controls {
  @apply absolute bottom-0 left-0 right-0 top-0 flex items-end justify-between opacity-0 transition-all;
  :deep() {
    i,
    svg {
      color: white;
    }
  }
}
.controls.always-show {
  @apply opacity-100;
}

a.item {
  .controls {
    @apply p-1;
  }
}
.control-button {
  @apply aspect-square h-10 w-10 overflow-hidden rounded-full bg-white/25 backdrop-blur;
  :deep() {
    i,
    svg {
      @apply text-white;
    }
    .favorite {
      @apply h-full;
    }
    button {
      @apply h-full w-full transition-all;
      &:hover,
      &:focus {
        background: theme("colors.gray.800") !important;
      }
    }
  }
}
</style>
